// src/components/InterviewChartRecent.js
import React, { useEffect, useState, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ResponsiveContainer,
} from "recharts";
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableHeader, TableRow
} from "@carbon/react";
import dayjs from "dayjs";
import CustomButton from "../components/CustomButton";

// ★ スキーマ（DBキー正準）
import {
  FUNCTION_GROUPS,
  getLabel,
  buildSeries,
  orderedLineKeysFromGroups,
  orderedRowKeysFromQids,
} from "../schema/aiInterviewSchema";

// ===== 表示範囲（月数） =================================================
const VIEW_RANGE_MONTHS = {
  "半年": 6,
  "1年": 12,
  "3年": 36,
  "全期間": null,
};

// ===== デモ用ダミーデータ（DB準拠 *_answer） ==========================
const DUMMY_RECORDS = [
  {
    ai_answer_date: "2022-11-15",
    ai_interview_msnq_att_answer: 7,
    ai_interview_msnq_mem_answer: 5,
    ai_interview_msnq_other_answer: 8,
    ai_interview_msnq_pb_answer: 6,
    ai_interview_amsq_answer: 4,
    ai_interview_msws1_answer: 6,
    ai_interview_msws2_answer: 7,
    ai_interview_msws3_answer: 5,
    ai_interview_mfis_cog_answer: 3,
    ai_interview_mfis_phy_answer: 6,
    ai_interview_mfis_soc_answer: 4,
  },
  {
    ai_answer_date: "2023-04-02",
    ai_interview_msnq_att_answer: 5,
    ai_interview_msnq_mem_answer: 6,
    ai_interview_msnq_other_answer: 7,
    ai_interview_msnq_pb_answer: 8,
    ai_interview_amsq_answer: 7,
    ai_interview_msws1_answer: 8,
    ai_interview_msws2_answer: 6,
    ai_interview_msws3_answer: 7,
    ai_interview_mfis_cog_answer: 4,
    ai_interview_mfis_phy_answer: 5,
    ai_interview_mfis_soc_answer: 6,
  },
  {
    ai_answer_date: "2023-10-20",
    ai_interview_msnq_att_answer: 6,
    ai_interview_msnq_mem_answer: 7,
    ai_interview_msnq_other_answer: 4,
    ai_interview_msnq_pb_answer: 5,
    ai_interview_amsq_answer: 8,
    ai_interview_msws1_answer: 5,
    ai_interview_msws2_answer: 7,
    ai_interview_msws3_answer: 6,
    ai_interview_mfis_cog_answer: 8,
    ai_interview_mfis_phy_answer: 7,
    ai_interview_mfis_soc_answer: 5,
  },
  {
    ai_answer_date: "2024-06-08",
    ai_interview_msnq_att_answer: 9,
    ai_interview_msnq_mem_answer: 8,
    ai_interview_msnq_other_answer: 6,
    ai_interview_msnq_pb_answer: 7,
    ai_interview_amsq_answer: 6,
    ai_interview_msws1_answer: 7,
    ai_interview_msws2_answer: 8,
    ai_interview_msws3_answer: 9,
    ai_interview_mfis_cog_answer: 5,
    ai_interview_mfis_phy_answer: 9,
    ai_interview_mfis_soc_answer: 7,
  },
  {
    ai_answer_date: "2025-02-27",
    ai_interview_msnq_att_answer: 4,
    ai_interview_msnq_mem_answer: 5,
    ai_interview_msnq_other_answer: 6,
    ai_interview_msnq_pb_answer: 3,
    ai_interview_amsq_answer: 8,
    ai_interview_msws1_answer: 9,
    ai_interview_msws2_answer: 7,
    ai_interview_msws3_answer: 8,
    ai_interview_mfis_cog_answer: 6,
    ai_interview_mfis_phy_answer: 4,
    ai_interview_mfis_soc_answer: 5,
  },
];

// ===== X軸ラベル =======================================================
const RotatedTick = ({ x, y, payload }) => (
  <g transform={`translate(${x},${y})`}>
    <text
      x={0}
      y={0}
      dy={10}
      textAnchor="end"
      fill="#666"
      transform="rotate(-45)"
      fontSize={12}
    >
      {dayjs(payload.value).format("YYYY-MM")}
    </text>
  </g>
);

// ===== メディアクエリ ==================================================
function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

// ===== テーブル（Carbon） =============================================
function renderCarbonScoreTable(data, orderedRowKeys) {
  if (data.length === 0) return null;
  const allDates = data.map((d) => dayjs(d.timestamp).format("YYYY-MM-DD"));

  return (
    <div style={{ overflowX: "auto", width: "100%", margin: "1rem 0" }}>
      <TableContainer>
        <div style={{ display: "inline-block", minWidth: "fit-content", whiteSpace: "nowrap" }}>
          <Table size="sm">
            <TableHead>
              <TableRow>
                <TableHeader>項目名</TableHeader>
                {allDates.map((d) => (
                  <TableHeader key={d}>{d}</TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {orderedRowKeys.map((key) => (
                <TableRow key={key}>
                  <TableCell>
                    {key === "walking_average" ? "歩行機能（平均）" : getLabel(key)}
                  </TableCell>
                  {data.map((d, idx) => (
                    <TableCell key={idx}>
                      {typeof d[key] === "number"
                        ? key === "walking_average"
                          ? d[key].toFixed(1)
                          : Math.round(d[key])
                        : "-"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TableContainer>
    </div>
  );
}

// ===== メイン ==========================================================
export default function InterviewChartRecent({
  apiData,
  // ✅ デフォは全グループ（= 9系列：歩行は平均+他は個別）
  selectedGroups = Object.keys(FUNCTION_GROUPS),
  // 任意：qid を直接渡すならこちら
  selectedQids,
  showChart = true,
  showTable = true,
  showRangeSwitcher = true,
  initialRange = "全期間",
  fixedMonths = null,
}) {
  const [records, setRecords] = useState([]);
  const [range, setRange] = useState(initialRange);
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    if (apiData) {
      // ai_answer_date配列と各スコア配列を結合して、records配列に変換
      const records = apiData.ai_answer_date.map((date, i) => ({
        ai_answer_date: date,
        ai_interview_msnq_att_answer: apiData.ai_interview_msnq_att_score[i],
        ai_interview_msnq_mem_answer: apiData.ai_interview_msnq_mem_score[i],
        ai_interview_msnq_other_answer: apiData.ai_interview_msnq_other_score[i],
        ai_interview_msnq_pb_answer: apiData.ai_interview_msnq_pb_score[i],
        ai_interview_amsq_answer: apiData.ai_interview_amsq_score[i],
        ai_interview_msws1_answer: apiData.ai_interview_msws1_score[i],
        ai_interview_msws2_answer: apiData.ai_interview_msws2_score[i],
        ai_interview_msws3_answer: apiData.ai_interview_msws3_score[i],
        ai_interview_mfis_cog_answer: apiData.ai_interview_mfis_cog_score[i],
        ai_interview_mfis_phy_answer: apiData.ai_interview_mfis_phy_score[i],
        ai_interview_mfis_soc_answer: apiData.ai_interview_mfis_soc_score[i],
      }));
      setRecords(records);
    }
  }, [apiData]);

  // ---- 対象qidの決定（selectedQids優先。なければselectedGroupsから）----
  const includedQids = useMemo(() => {
    if (selectedQids?.length) return Array.from(new Set(selectedQids));
    return Array.from(
      new Set(selectedGroups.flatMap((g) => FUNCTION_GROUPS[g] ?? []))
    );
  }, [selectedGroups, selectedQids]);

  // ✅ 歩行平均を出すか？（グループ指定 or qid指定のどちらでも検知）
  const includeWalkingAverage = useMemo(() => {
    const walkingQids = FUNCTION_GROUPS["歩行機能"] ?? [];
    const hasByGroup = selectedGroups.includes("歩行機能");
    const hasByQid = includedQids.some((q) => walkingQids.includes(q));
    return hasByGroup || hasByQid;
  }, [selectedGroups, includedQids]);

  // 期間境界
  const allAnswerTimes = records
    .map((r) => dayjs(r.ai_answer_date).valueOf())
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);

  const hasData = allAnswerTimes.length > 0;
  const earliest = hasData ? allAnswerTimes[0] : dayjs().valueOf();
  const latest = hasData ? allAnswerTimes[allAnswerTimes.length - 1] : dayjs().valueOf();

  let fromDateMs;
  if (fixedMonths != null) {
    const candidate = dayjs(latest).subtract(fixedMonths, "month").valueOf();
    fromDateMs = Math.min(candidate, earliest);
  } else {
    const months = VIEW_RANGE_MONTHS[range];
    fromDateMs = months == null
      ? dayjs(earliest).valueOf()
      : dayjs(latest).subtract(months, "month").valueOf();
  }

  // records -> series（timestamp, qid: value, walking_average?）
  const seriesAll = useMemo(
    () => buildSeries(records, includedQids, { includeWalkingAverage }),
    [records, includedQids, includeWalkingAverage]
  );

  // 期間でフィルタ
  const filtered = seriesAll.filter((row) => row.timestamp >= fromDateMs);

  // ---- X軸の月次目盛 ----
  const allDates = filtered.map((d) => d.timestamp);
  const minTimestamp = allDates.length ? Math.min(...allDates) : fromDateMs;
  const maxTimestamp = allDates.length ? Math.max(...allDates) : latest;

  const monthTicks = [];
  const monthStep = isMobile ? 2 : 1;
  let cursor = dayjs(minTimestamp).startOf("month");
  const endMonth = dayjs(maxTimestamp).endOf("month");
  while (cursor.valueOf() <= endMonth.valueOf()) {
    monthTicks.push(cursor.valueOf());
    cursor = cursor.add(monthStep, "month");
  }

  // ---- 折れ線キー順（Legend順もこれに従う）----
  const orderedLineKeys = useMemo(
    () => orderedLineKeysFromGroups(selectedGroups, includedQids),
    [selectedGroups, includedQids]
  );

  // ---- テーブル行キー（歩行は平均、それ以外は個別）----
  const orderedRowKeys = useMemo(
    () => orderedRowKeysFromQids(includedQids, { includeWalkingAverage }),
    [includedQids, includeWalkingAverage]
  );

  return (
    <div style={{ maxWidth: "1000px", margin: "1rem auto" }}>
      {/* 範囲切り替え（固定レンジ時は非表示） */}
      {showRangeSwitcher && fixedMonths == null && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
          {Object.keys(VIEW_RANGE_MONTHS).map((label) => (
            <CustomButton
              kind={range === label ? "primary" : "secondary"}
              key={label}
              onClick={() => setRange(label)}
              size="sm"
              style={{ minWidth: 80, flexGrow: 1, maxWidth: 100, paddingLeft: 0, paddingRight: 0 }}
            >
              <span style={{ textAlign: "center", width: "100%" }}>{label}</span>
            </CustomButton>
          ))}
        </div>
      )}

      {/* グラフ（9系列：歩行=平均、他=個別） */}
      {showChart && (
        <ResponsiveContainer width="100%" height={450}>
          <LineChart data={filtered} style={{ transform: "translateX(-1rem)" }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="timestamp"
              scale="time"
              domain={[
                dayjs(minTimestamp).startOf("month").valueOf(),
                dayjs(maxTimestamp).endOf("month").valueOf()
              ]}
              ticks={monthTicks}
              tickFormatter={(unixTime) => dayjs(unixTime).format("YYYY-MM")}
              interval={0}
              tick={<RotatedTick />}
            />
            <YAxis domain={[1, 10]} ticks={[1,2,3,4,5,6,7,8,9,10]} />
            <Tooltip labelFormatter={(unixTime) => dayjs(unixTime).format("YYYY-MM-DD")} />

            <Legend
              wrapperStyle={{
                padding: "1.5rem 0 0 0",
                lineHeight: "1.5rem",
                transform: "translateX(1rem)",
              }}
            />

            {includeWalkingAverage && (
              <Line
                type="linear"
                dataKey="walking_average"
                name="歩行機能（平均）"
                stroke="#82ca9d"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            )}

            {orderedLineKeys.map((qid) => (
              <Line
                key={qid}
                type="linear"
                dataKey={qid}
                name={getLabel(qid)}
                stroke="#8884d8"
                dot={{ r: 3 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}

      {/* 表（9列：歩行=平均、それ以外=個別） */}
      {showTable && renderCarbonScoreTable(filtered, orderedRowKeys)}
    </div>
  );
}
